import React, { useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFileContract } from '@fortawesome/free-solid-svg-icons';

const TermsOfService: React.FC = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="policy-page">
      <Container className="py-5">
        <Row>
          <Col lg={10} className="mx-auto">
            <Link to="/" className="btn btn-link mb-4">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </Link>

            <Card className="policy-content">
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  <FontAwesomeIcon icon={faFileContract} size="3x" className="text-primary mb-3" />
                  <h1>Terms of Service</h1>
                  <p className="text-muted">Effective Date: April 16, 2026</p>
                  <p className="text-muted">KairosIQ AI, Inc. — A Delaware Corporation</p>
                </div>

                <div className="policy-section">
                  <h2>1. Agreement to Terms</h2>
                  <p>
                    These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and KairosIQ AI, Inc. ("KairosIQ," "Company," "we," "us," or "our"), a Delaware corporation. By accessing or using the AI Search Portal platform (aisearchportal.com), chat.realestate, or any associated services (collectively, the "Platform"), you agree to be bound by these Terms.
                  </p>
                  <p>
                    KairosIQ AI Realty, a licensed Arizona real estate brokerage (License #CO721005000), operates under the corporate umbrella of KairosIQ AI, Inc. Real estate brokerage services are provided through KairosIQ AI Realty; software and AI services are provided through KairosIQ AI, Inc.
                  </p>
                  <p>
                    <strong>IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE PLATFORM.</strong>
                  </p>
                </div>

                <div className="policy-section">
                  <h2>2. Description of Services</h2>
                  <p>The Platform provides the following services:</p>
                  <ul>
                    <li><strong>Broker Compliance Portal</strong> — Document management, transaction tracking, agent management, and AI-assisted compliance review for licensed real estate brokerages.</li>
                    <li><strong>Agent Portal</strong> — CRM, client management, deal pipeline management, email integration, and AI-powered lead coaching for licensed real estate agents.</li>
                    <li><strong>AI Search Portal (chat.realestate)</strong> — AI-powered property search and buyer lead generation for consumers.</li>
                    <li><strong>MLS Data Integration</strong> — Property listings and market data sourced from Multiple Listing Service providers, including but not limited to the Arizona Regional Multiple Listing Service (ARMLS).</li>
                    <li><strong>AI Document Review</strong> — Automated analysis of real estate transaction documents for compliance gaps and completeness.</li>
                    <li><strong>AI Lead Coach</strong> — Automated analysis, recommendations, and draft communications for client relationship management.</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h2>3. AI Services Disclaimer — CRITICAL</h2>
                  <p>
                    <strong>THE AI FEATURES OF THIS PLATFORM, INCLUDING BUT NOT LIMITED TO DOCUMENT REVIEW, LEAD COACHING, PROPERTY VALUATIONS, MARKET ANALYSIS, AND AUTOMATED RECOMMENDATIONS, ARE PROVIDED FOR INFORMATIONAL AND ASSISTIVE PURPOSES ONLY.</strong>
                  </p>
                  <p>
                    AI-generated content, analysis, recommendations, and drafts:
                  </p>
                  <ul>
                    <li><strong>Are NOT legal advice.</strong> They do not constitute legal counsel and should not be treated as such. Users should consult a licensed attorney for legal matters.</li>
                    <li><strong>Are NOT a substitute for professional judgment.</strong> Licensed real estate professionals are solely responsible for verifying all AI-generated information, reviewing all documents, and making independent professional decisions.</li>
                    <li><strong>May contain errors, inaccuracies, or omissions.</strong> AI models can produce incorrect, incomplete, or misleading outputs. Users must independently verify all AI-generated content before acting on it.</li>
                    <li><strong>Do NOT constitute a brokerage relationship.</strong> Use of AI features does not create an agency, fiduciary, or brokerage relationship between the User and KairosIQ.</li>
                  </ul>
                  <p>
                    <strong>KairosIQ expressly disclaims all liability for decisions made based on AI-generated analysis, property valuations, market predictions, compliance assessments, email drafts, or automated recommendations.</strong>
                  </p>
                </div>

                <div className="policy-section">
                  <h2>4. Fair Housing Compliance</h2>
                  <p>
                    <strong>THIS PLATFORM IS NOT A SUBSTITUTE FOR FAIR HOUSING COMPLIANCE TRAINING, EDUCATION, OR LEGAL COUNSEL.</strong>
                  </p>
                  <p>
                    While the Platform includes AI-powered monitoring tools designed to help identify potential fair housing concerns in communications and documents, these tools:
                  </p>
                  <ul>
                    <li>Are assistive in nature and may not detect all fair housing violations.</li>
                    <li>Do not guarantee compliance with the Fair Housing Act, state fair housing laws, or any other anti-discrimination statutes.</li>
                    <li>Cannot replace proper fair housing training for real estate licensees.</li>
                  </ul>
                  <p>
                    Users remain <strong>solely and exclusively responsible</strong> for compliance with all applicable fair housing laws, including the Federal Fair Housing Act (42 U.S.C. §§ 3601-3619), state and local fair housing statutes, and all regulations promulgated thereunder. KairosIQ shall not be liable for any fair housing violations committed by Users of the Platform.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>5. Data Collection, Processing, and Privacy</h2>
                  <h3>5.1 Information We Collect</h3>
                  <p>By using the Platform, you consent to the collection and processing of:</p>
                  <ul>
                    <li>Account information (name, email, phone, license number, brokerage affiliation)</li>
                    <li>Transaction data (property information, deal stages, closing details, commission structures)</li>
                    <li>Documents uploaded to the Platform (contracts, disclosures, inspection reports, and other transaction files)</li>
                    <li>Communications data when email accounts are connected (Gmail, Outlook — subject to Section 5.2)</li>
                    <li>Usage data (login timestamps, IP addresses, user agent strings, feature usage patterns)</li>
                    <li>MLS data associated with your account and transactions</li>
                  </ul>

                  <h3>5.2 Email Integration and Access</h3>
                  <p>
                    By connecting your Gmail or Microsoft Outlook account to the Platform, you explicitly authorize KairosIQ to:
                  </p>
                  <ul>
                    <li>Access, read, scan, and index email communications for the purpose of CRM management, deal management, and lead discovery.</li>
                    <li>Store email content in encrypted form (AES-256-GCM) within our secure database.</li>
                    <li>Use email data to match communications with leads, clients, and transaction parties.</li>
                    <li>Process email content through AI models for classification, analysis, and recommendation generation.</li>
                  </ul>
                  <p>
                    Email data is stored in compliance with CASA Tier 2 audit requirements. All access to email data is logged for audit purposes. You may disconnect your email account at any time through your Dashboard Settings.
                  </p>

                  <h3>5.3 De-Identification and AI Processing</h3>
                  <p>
                    All client communications and personal data processed by AI models are de-identified (PII removed) before being sent to AI services. Raw client data is never sent to third-party AI providers. De-identification is applied to: names, phone numbers, email addresses, social security numbers, financial account numbers, and physical addresses of clients.
                  </p>

                  <h3>5.4 Data Retention</h3>
                  <p>
                    Account data and transaction records are retained for the duration of your subscription plus five (5) years, consistent with real estate record-keeping requirements. Email data is retained for the duration of your email connection. Upon disconnection or account termination, email data is scheduled for deletion within thirty (30) days.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>6. Security and Authentication</h2>
                  <p>
                    KairosIQ employs industry-standard security measures including:
                  </p>
                  <ul>
                    <li>Password policy enforcement (minimum 12 characters, complexity requirements)</li>
                    <li>AES-256-GCM encryption for stored email content and OAuth tokens</li>
                    <li>Authentication event logging (login attempts, password resets, IP addresses) for security audit purposes</li>
                    <li>JWT-based session management with server-side account status verification</li>
                    <li>AWS Secrets Manager for credential storage</li>
                  </ul>
                  <p>
                    Users are responsible for maintaining the confidentiality of their login credentials. You agree to notify KairosIQ immediately of any unauthorized access to your account.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>7. MLS Data Terms</h2>
                  <p>
                    Property listing data displayed on the Platform is provided by Multiple Listing Service organizations, including the Arizona Regional Multiple Listing Service, Inc. (ARMLS). Use of MLS data is subject to:
                  </p>
                  <ul>
                    <li>The terms and conditions of the applicable MLS organization.</li>
                    <li>Virtual Office Website (VOW) compliance requirements.</li>
                    <li>Users must maintain valid, active MLS membership to access MLS data through the Platform.</li>
                    <li>MLS data may not be redistributed, scraped, or used for purposes other than those authorized by the MLS.</li>
                  </ul>
                  <p>
                    KairosIQ does not guarantee the accuracy, completeness, or timeliness of MLS data. Property information is subject to change without notice.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>8. Subscription and Payment</h2>
                  <ul>
                    <li>Subscription fees are billed monthly through Stripe. Pricing is subject to change with thirty (30) days' notice.</li>
                    <li>Broker Portal subscriptions include compliance features for all agents at no additional per-agent cost. Optional Agent Hub seats are billed at the then-current per-seat rate.</li>
                    <li>AI Search and AI Pro tiers are billed directly to the agent's payment method.</li>
                    <li>Refunds are issued at KairosIQ's sole discretion. No refunds are provided for partial billing periods.</li>
                    <li>Failure to pay may result in account suspension or termination.</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h2>9. Account Termination and Suspension</h2>
                  <p>
                    KairosIQ reserves the right to suspend or terminate any User account at any time, with or without cause, including but not limited to:
                  </p>
                  <ul>
                    <li>Violation of these Terms</li>
                    <li>Fraudulent or unlawful activity</li>
                    <li>Non-payment of subscription fees</li>
                    <li>Revocation or suspension of the User's real estate license</li>
                    <li>Fair housing violations or discriminatory conduct</li>
                    <li>Misuse of AI features or platform data</li>
                  </ul>
                  <p>
                    Account termination is effective immediately. Terminated users will receive the message: "Unable to sign in. Please contact KairosIQ Support at support@kairosiq.ai or call (520) 251-9595."
                  </p>
                </div>

                <div className="policy-section">
                  <h2>10. Limitation of Liability and Hold Harmless</h2>
                  <p>
                    <strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, KAIROSIQ AI, INC., ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES (INCLUDING KAIROSIQ AI REALTY) SHALL NOT BE LIABLE FOR:</strong>
                  </p>
                  <ul>
                    <li>Any direct, indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform.</li>
                    <li>Decisions made based on AI-generated analysis, recommendations, property valuations, or automated communications.</li>
                    <li>Loss of data, revenue, profits, or business opportunities.</li>
                    <li>Errors, inaccuracies, or omissions in MLS data, property information, or market analysis.</li>
                    <li>Fair housing violations, regulatory penalties, or legal claims arising from the User's conduct.</li>
                    <li>Unauthorized access to your account due to compromised credentials.</li>
                    <li>Service interruptions, downtime, or data loss.</li>
                  </ul>
                  <p>
                    <strong>YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS</strong> KairosIQ AI, Inc., KairosIQ AI Realty, and their respective officers, directors, employees, and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from: (a) your use of the Platform; (b) your violation of these Terms; (c) your violation of any applicable law, regulation, or third-party rights; or (d) any transaction, communication, or action taken based on information provided by the Platform.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>11. Disclaimer of Warranties</h2>
                  <p>
                    THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. KAIROSIQ DOES NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>12. Intellectual Property</h2>
                  <p>
                    All intellectual property rights in the Platform, including software, AI models, algorithms, designs, trademarks, and content (excluding User-uploaded data) are owned by KairosIQ AI, Inc. Users are granted a limited, non-exclusive, non-transferable license to use the Platform for the duration of their subscription.
                  </p>
                  <p>
                    Users retain ownership of their data, documents, and content uploaded to the Platform. By uploading content, you grant KairosIQ a non-exclusive license to process, store, analyze, and display such content as necessary to provide the Platform services.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>13. Governing Law and Dispute Resolution</h2>
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of the State of Arizona, without regard to conflict of law principles. Any disputes arising under or in connection with these Terms shall be resolved through binding arbitration in Maricopa County, Arizona, in accordance with the rules of the American Arbitration Association. The prevailing party in any arbitration or legal proceeding shall be entitled to recover reasonable attorneys' fees and costs.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>14. Modifications to Terms</h2>
                  <p>
                    KairosIQ reserves the right to modify these Terms at any time. Material changes will be communicated via email to the address associated with your account at least thirty (30) days before taking effect. Continued use of the Platform after the effective date of any modification constitutes acceptance of the modified Terms.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>15. Severability</h2>
                  <p>
                    If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that the remaining Terms remain in full force and effect.
                  </p>
                </div>

                <div className="policy-section">
                  <h2>16. Contact Information</h2>
                  <p>
                    For questions about these Terms, contact:<br />
                    <strong>KairosIQ AI, Inc.</strong><br />
                    Email: support@kairosiq.ai<br />
                    Phone: (520) 251-9595<br />
                    Address: 2700 S Gilbert Rd, Ste 5, Chandler, AZ 85286
                  </p>
                </div>

                <div className="policy-section text-center mt-5">
                  <p className="text-muted">
                    © 2026 KairosIQ AI, Inc. All rights reserved.<br />
                    KairosIQ AI Realty — Arizona Broker License #CO721005000
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TermsOfService;
